import React, { useState, useCallback, useEffect } from 'react';
import Toolbox from './Toolbox';
import PropertyEditor from './PropertyEditor';
import { HiPlus, HiAdjustmentsHorizontal } from "react-icons/hi2";

const Sidebar = () => {
  const [activeTab, setActiveTab] = useState('elements');
  const [width, setWidth] = useState(320);
  const [isResizing, setIsResizing] = useState(false);

  const startResizing = useCallback((e) => {
    setIsResizing(true);
    e.preventDefault();
  }, []);

  const stopResizing = useCallback(() => {
    setIsResizing(false);
  }, []);

  const resize = useCallback((e) => {
    if (isResizing) {
      const newWidth = e.clientX;
      if (newWidth >= 280 && newWidth <= 600) {
        setWidth(newWidth);
      }
    }
  }, [isResizing]);

  useEffect(() => {
    if (isResizing) {
      window.addEventListener('mousemove', resize);
      window.addEventListener('mouseup', stopResizing);
    }
    return () => {
      window.removeEventListener('mousemove', resize);
      window.removeEventListener('mouseup', stopResizing);
    };
  }, [isResizing, resize, stopResizing]);

  return (
    <div 
      style={{ width: `${width}px` }}
      className="bg-[#0e1318] border-t-2 border-r-2 border-b-2 border-white flex flex-col h-full relative z-40 transition-[box-shadow] duration-300 select-none shadow-[20px_0_60px_rgba(0,0,0,0.2)]"
    >
      {/* Resize Handle */}
      <div
        onMouseDown={startResizing}
        className={`absolute top-0 -right-1 w-2 h-full cursor-col-resize z-50 transition-colors ${isResizing ? 'bg-[#8B3DFF]/40' : 'hover:bg-[#8B3DFF]/20'}`}
      />

      {/* Professional Header */}
      <div className="p-6 border-b border-white/5 flex items-center justify-between bg-black/10">
        <div className="flex items-center gap-2">
          <div className="w-2.5 h-6 bg-gradient-to-b from-[#8B3DFF] to-[#00C4CC] rounded-full shadow-[0_0_15px_rgba(139,61,255,0.4)]" />
          <h2 className="text-[11px] font-black text-white uppercase tracking-[0.25em]">Editor</h2>
        </div>
        
        <div className="flex bg-white/5 p-1 rounded-2xl backdrop-blur-md border border-white/10">
          <button
            onClick={() => setActiveTab('elements')}
            title="Components"
            className={`p-2.5 rounded-xl transition-all duration-300 ${activeTab === 'elements' ? 'bg-[#8B3DFF] text-white shadow-[0_8px_16px_rgba(139,61,255,0.3)]' : 'text-slate-500 hover:text-slate-300'}`}
          >
            <HiPlus className="w-5 h-5" />
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            title="Settings"
            className={`p-2.5 rounded-xl transition-all duration-300 ${activeTab === 'settings' ? 'bg-[#8B3DFF] text-white shadow-[0_8px_16px_rgba(139,61,255,0.3)]' : 'text-slate-500 hover:text-slate-300'}`}
          >
            <HiAdjustmentsHorizontal className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-y-auto bg-transparent">
        <div className="animate-in fade-in zoom-in-95 duration-500 px-2 py-4">
          {activeTab === 'elements' ? <Toolbox /> : <PropertyEditor />}
        </div>
      </div>
      
      {/* Bottom Visual Cue */}
      <div className="p-6 border-t border-white/5 bg-black/5">
        <div className="bg-white/5 border border-white/5 rounded-[2rem] p-5 flex flex-col items-center text-center group transition-all hover:border-[#8B3DFF]/30">
           <div className="w-10 h-1 bg-white/10 rounded-full mb-4 transition-colors group-hover:bg-[#8B3DFF]/30" />
           <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest leading-relaxed">
             Drag to Canvas
           </p>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
